<template>
	<div 
		class="upload-box"
		:class="[
			`theme-${theme}`
		]"
		>

		<div class="inner-box">

			<div class="upload-items" v-if="files.length > 0">
				<UploadingRow
					v-for="(file, index) in files"
					v-bind:key="file.file.uniqueIdentifier + index"
					:file="file.file"
					:status="file.status"
					:progress="file.progress"
					:size="file.size"
					@cancel="cancelFile"
					:theme="theme"
					/>
			</div>

			<div 
				class="video-dropzone" 
				ref="videodropzone"
				:class="[
					normalisedFileCount == 0 || normalisedFileCount > 0 && maxFiles > 1 ? 'show' : 'hide'
				]"
				>

				<div class="inner" v-if="normalisedFileCount == 0 && files.length == 0">
					<div class="icon">
						<i class="fal fa-file-upload"></i>
					</div>

					<div class="text">
						{{ maxFiles > 1 ? 'Drag and drop or click to browse to choose one or more files' : 'Drag and drop or click to browse to choose a file.' }}
					</div>
				</div>

				<div class="inner add-more" v-else>
					<div class="text">
						Drag and drop or click to browse to upload more files.
					</div>
				</div>

			</div>

		</div>

		

		<InputText
			v-if="withName"
			label="Friendly Name"
			placeholder="Please give the file a user friendly name"
			type="text"
			v-model="headers.nice_name"
			autocomplete="disabled"
			:darkMode="true"
			:disabled="!showUploadButton"
			/>
		<div class="button-container">
			<Button
				label="Upload another file"
				color="blue"
				size="regular"
				icon="plus"
				@click="reset"
				v-if="!showUploadButton && ['enquiry-form', 'doc-library'].includes(designation)"
				/>

			<Button
				label="Begin Upload"
				color="blue"
				size="regular"
				icon="upload"
				@click="startUpload"
				:disabled="files.length == 0 || normalisedFileCount == filesUploadedCount || (withName && headers.nice_name == '')"
				v-if="showUploadButton"
				/>

			<slot></slot>
		</div>


	</div>
</template>

<script>

import Resumable from 'resumablejs'

import UploadingRow from '@/Components/Upload/UploadingRow'
import InputText from "@/Components/InputText"
import Button from '@/Components/Button'

export default {
	components: {
		UploadingRow,
		Button,
		InputText
	},
	props: {
		case: Object,
		designation: String,
		maxFiles: {
			type: Number,
			default: 1
		},
		theme: {
			type: String,
			default: 'light'
		},
		withName: {
			type: Boolean,
			default: false
		}
	},
	data: () => {
		return {
			headers: {
				'X-CSRF-TOKEN': '',
				'_token': '',
				nice_name: ''
			},
			files: [],
			r: false,
			showUploadButton: true,
			filesUploadedCount: 0,
			cancelledFileCount: 0,
		}
	},
	mounted() {

		this.headers['X-CSRF-TOKEN'] = this.$page.props.csrf_token
		this.headers['_token'] = this.$page.props.csrf_token
		this.headers['token'] = this.$page.props.csrf_token
		this.headers['case'] = this.case ? this.case.id : ''
		this.headers['designation'] = this.designation
		this.headers['Accept'] = 'application/json'

		// init resumablejs on mount
		this.r = new Resumable({
			target: '/api/upload',
			query: this.headers,
			maxChunkRetries: 10,
			testChunks: false,
			throttleProgressCallbacks: 1,
			simultaneousUploads: 1,
			maxFiles: this.maxFiles,
			withCredentials: false,
			forceChunkSize: false
		})

		// Resumable.js isn't supported, fall back on a different method
		if(!this.r.support) return alert('Your browser doesn\'t support chunked uploads. Please use a modern web browser such as Chrome or Firefox. This will not only make this feature work here, but will improve your web experience and overall security on the internet going forwards.')

		this.r.assignBrowse(this.$refs.videodropzone);
		this.r.assignDrop(this.$refs.videodropzone);
		// set up event listeners to feed into vues reactivity
		this.r.on('fileAdded', (file, event) => {
			file.hasUploaded = false
			this.$emit('filesAdded')
			// console.log('this.files', this.files)
			// keep a list of files with some extra data that we can use as props
			this.files.push({
				file,
				status: 'pending',
				progress: 0,
				size: 0
			})
		})
		this.r.on('fileSuccess', (file, event) => {
			this.findFile(file).status = 'success'
			// console.log('upload complete', {
			// 	file: this.findFile(file),
			// 	frontEndSize: this.findFile(file).size,
			// 	event: event
			// })
			this.$emit('done', JSON.parse(event))
			this.filesUploadedCount++
		})
		this.r.on('fileError', (file, event) => {
			this.findFile(file).status = 'error'
			this.showUploadButton = true
			this.cancelledFileCount++
		})
		this.r.on('fileRetry', (file, event) => {
			this.findFile(file).status = 'retrying'
		})
		this.r.on('fileProgress', (file) => {
			// console.log('fileProgress', file)
			this.findFile(file).size = file.size

			let localFile = this.findFile(file)
			// if we are doing multiple chunks we may get a lower progress number if one chunk response comes back early
			let progress = file.progress()
			if( progress > localFile.progress){
				localFile.progress = progress
			}
		})

		this.r.on('uploadStart', () => {
			for(let file in this.files){
				if(this.files[file].status != 'canceled' && this.files[file].status != 'success'){
					this.files[file].status = 'uploading'
				}
			}
		})

	},
	methods: {
		// finds the file in the local files array
		findFile(file){
			return this.files.find(item => item.file.uniqueIdentifier === file.uniqueIdentifier && item.status !== 'canceled') ?? {}
		},
		// cancel an individual file
		cancelFile(file){
			this.findFile(file).status = 'canceled'
			file.cancel()

			this.cancelledFileCount++
		},
		startUpload(){
			if((this.withName && this.headers.nice_name != '') || !this.withName){
				if(this.normalisedFileCount > 0){
					if(this.maxFiles == 1){
						this.showUploadButton = false
					}
					this.r.upload()
				}
			}
		},
		reset(){
			this.files = []
			this.showUploadButton = true
			this.filesUploadedCount = 0
			this.cancelledFileCount = 0
			this.headers.nice_name = ''
		}
	},
	computed: {
		normalisedFileCount(){
			return this.files.length - this.cancelledFileCount
		}
	}
}

</script>

<style scoped lang="sass">

.upload-box
	width: 100%

	.inner-box
		min-height: 150px
		display: flex
		align-items: center
		justify-content: center
		border: 2px dotted rgba(#b9b9b9, 0.4)
		border-radius: 4px
		flex-direction: column
		margin-bottom: 20px
		padding: 20px

		@media (max-width: 768px)
			min-height: 120px

	.icon
		color: #252525
		font-size: 50px
		margin-bottom: 10px

		@media (max-width: 768px)
			font-size: 40px

	.text
		font-family: 'Roboto Condensed', sans-serif
		font-size: 21px
		font-weight: 300
		color: #252525
		line-height: 1.2
		text-align: center

		@media (max-width: 768px)
			font-size: 16px

	.upload-items
		width: 100%
		padding: 0

	.video-dropzone
		width: 100%
		height: 100%
		display: flex

		&.show
			visibility: visible

		&.hide
			visibility: hidden
			height: 0

		.inner
			width: 100%
			height: 100%
			display: flex
			align-items: center
			justify-content: center
			flex-direction: column
			cursor: pointer

			&.add-more
				background-color: rgba(#000,0.02)
				border-radius: 8px
				margin-top: 20px
				padding: 15px

				.text
					font-size: 18px

	&.theme-dark
		.icon
			color: #f8f8f8

		.text
			color: #f8f8f8

		.video-dropzone
			.inner
				&.add-more
					background-color: rgba(#fff,0.02)

	.button-container
		display: flex
		gap: 20px
		flex-wrap: wrap
		justify-content: center


</style>